// src/components/admin/ServiceModal.jsx - Modified for Server-Side OG Generation
// SEO/OG fields are stored in the database - server uses them for OG generation

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  FiX,
  FiSave,
  FiPlus,
  FiTag,
  FiAlertCircle,
  FiImage,
  FiTrendingUp,
  FiCopy,
  FiDollarSign,
  FiClock,
  FiPackage,
  FiUsers,
  FiCheckCircle,
  FiStar,
  FiInfo,
  FiLoader,
  FiUpload,
  FiGrid,
  FiList,
  FiEdit2,
  FiTrash2,
  FiShare2,
  FiZap,
} from 'react-icons/fi';
import { TfiLightBulb } from 'react-icons/tfi';
import { useTheme } from '../../contexts/ThemeContext';
import { useToast } from '../../hooks/useToast';
import ImageUploader from './ImageUploader';
import { TagManager } from '../admin/blog/TagManager';

// ============================================
// SOCIAL PREVIEW INDICATOR (UI Only)
// ============================================
const SocialPreviewIndicator = ({ ogImage, ogTitle, ogDescription }) => {
  const { actualTheme } = useTheme();
  const isDark = actualTheme === 'dark';

  if (!ogImage && !ogTitle && !ogDescription) {
    return null;
  }

  return (
    <div
      className="mt-2 p-3 rounded-xl border"
      style={{
        borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
        backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
      }}
    >
      <div className="flex items-center gap-2 mb-2">
        <FiShare2 className="w-3.5 h-3.5 text-blue-500" />
        <span
          className="text-[10px] font-medium"
          style={{ color: isDark ? '#ffffff' : '#0a0a0a' }}
        >
          Social Sharing Preview
        </span>
        <span
          className="text-[9px]"
          style={{
            color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
          }}
        >
          (OG tags generated server-side)
        </span>
      </div>
      <div
        className="relative rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800"
        style={{ aspectRatio: '1.91/1' }}
      >
        {ogImage ? (
          <>
            <img
              src={ogImage}
              alt="OG Preview"
              className="w-full h-full object-cover"
            />
            <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 via-black/50 to-transparent">
              {ogTitle && (
                <p className="text-white font-semibold text-sm line-clamp-1">
                  {ogTitle}
                </p>
              )}
              {ogDescription && (
                <p className="text-white/70 text-xs line-clamp-1 mt-0.5">
                  {ogDescription}
                </p>
              )}
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <FiImage className="w-8 h-8 mx-auto opacity-30" />
              <p
                className="text-xs mt-2 opacity-50"
                style={{ color: isDark ? '#ffffff' : '#0a0a0a' }}
              >
                No OG image set
              </p>
              <p
                className="text-[10px] opacity-30"
                style={{ color: isDark ? '#ffffff' : '#0a0a0a' }}
              >
                Upload a service image to auto-generate
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// CONSTANTS
// ============================================

const CATEGORY_OPTIONS = [
  {
    value: 'branding',
    label: 'Brand Design',
    icon: '🎨',
    description: 'Logo, identity, brand guidelines',
  },
  {
    value: 'product',
    label: 'Product Visual',
    icon: '📱',
    description: 'UI/UX, product design, mockups',
  },
  {
    value: 'others',
    label: 'Other Works',
    icon: '✨',
    description: 'Illustrations, animations, etc.',
  },
];

const ICON_OPTIONS = [
  'FiPackage',
  'FiGrid',
  'FiTrendingUp',
  'FiTag',
  'FiUsers',
  'FiHeart',
  'FiStar',
  'FiZap',
  'FiEye',
  'FiCode',
  'FiMonitor',
  'FiSmartphone',
  'FiGlobe',
  'FiImage',
  'FiVideo',
  'FiBarChart2',
  'FiShield',
  'FiMessageCircle',
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active', color: 'green' },
  { value: 'draft', label: 'Draft', color: 'gray' },
];

// ============================================
// MAIN COMPONENT
// ============================================

function ServiceModal({
  isOpen,
  onClose,
  service,
  onSave,
  onUploadImage,
  onDeleteImage,
  onDeleteTempImage,
  isEditing,
  saving,
  serviceId = null,
}) {
  const { actualTheme } = useTheme();
  const { showToast } = useToast();
  const modalRef = useRef(null);
  const textareaRef = useRef(null);

  // ============================================
  // STATE
  // ============================================

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    duration: '',
    category: 'branding',
    features: [],
    popular: false,
    icon: 'FiPackage',
    imageFile: null,
    order: 0,
    status: 'active',
    tags: [],
    targetAudience: [],
    deliverables: [],
    revisions: '',
    support: '',
    // SEO & OG Fields - Stored for server-side OG generation
    seoTitle: '',
    seoDescription: '',
    seoKeywords: [],
    ogTitle: '',
    ogDescription: '',
    ogImage: '',
    twitterTitle: '',
    twitterDescription: '',
    twitterImage: '',
  });

  const [imageItems, setImageItems] = useState([]);
  const [thumbnailIndex, setThumbnailIndex] = useState(-1);
  const [existingImageUrl, setExistingImageUrl] = useState(null);
  const [errors, setErrors] = useState({});
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [uploadingImageId, setUploadingImageId] = useState(null);

  // Bulk input states
  const [newFeature, setNewFeature] = useState('');
  const [newTag, setNewTag] = useState('');
  const [newAudience, setNewAudience] = useState('');
  const [newDeliverable, setNewDeliverable] = useState('');

  // Suggestions
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestedTags, setSuggestedTags] = useState([]);
  const [suggestionLoading, setSuggestionLoading] = useState(false);

  // ============================================
  // HELPERS
  // ============================================

  const parseBulkInput = input => {
    if (!input.trim()) return [];
    let items = input.split(/[,#\n]+/);
    items = items
      .map(item => item.trim().replace(/^#/, '').trim())
      .filter(item => item.length > 0);
    return [...new Set(items)];
  };

  const getThumbnailUrl = () => {
    if (imageItems.length === 0) return '';
    const thumbIndex =
      thumbnailIndex >= 0 && thumbnailIndex < imageItems.length
        ? thumbnailIndex
        : 0;
    const thumb = imageItems[thumbIndex];
    return thumb?.uploadedUrl || thumb?.url || '';
  };

  // ============================================
  // AUTO-GENERATE SEO FIELDS
  // ============================================

  const autoGenerateSEOFields = useCallback(() => {
    const title = formData.name || '';
    const description = formData.description || '';
    const tags = formData.tags || [];
    const image =
      formData.ogImage || existingImageUrl || getThumbnailUrl() || '';

    const updates = {};

    // SEO Title
    if (!formData.seoTitle && title) {
      updates.seoTitle =
        title.length > 60
          ? title.substring(0, 57) + '... - WD Hejbul'
          : title + ' - WD Hejbul';
    }

    // SEO Description
    if (!formData.seoDescription && description) {
      updates.seoDescription =
        description.length > 160
          ? description.substring(0, 157) + '...'
          : description;
    }

    // OG Title
    if (!formData.ogTitle && title) {
      updates.ogTitle =
        title.length > 90 ? title.substring(0, 87) + '...' : title;
    }

    // OG Description
    if (!formData.ogDescription && description) {
      updates.ogDescription =
        description.length > 200
          ? description.substring(0, 197) + '...'
          : description;
    }

    // OG Image
    if (!formData.ogImage && image) {
      updates.ogImage = image;
    }

    // Twitter fields
    if (!formData.twitterTitle && (updates.ogTitle || formData.ogTitle)) {
      updates.twitterTitle = updates.ogTitle || formData.ogTitle;
    }
    if (
      !formData.twitterDescription &&
      (updates.ogDescription || formData.ogDescription)
    ) {
      updates.twitterDescription =
        updates.ogDescription || formData.ogDescription;
    }
    if (!formData.twitterImage && (updates.ogImage || formData.ogImage)) {
      updates.twitterImage = updates.ogImage || formData.ogImage;
    }

    // SEO Keywords
    if (
      (!formData.seoKeywords || formData.seoKeywords.length === 0) &&
      tags.length > 0
    ) {
      updates.seoKeywords = tags.slice(0, 10);
    }

    if (Object.keys(updates).length > 0) {
      setFormData(prev => ({ ...prev, ...updates }));
      showToast('SEO fields auto-generated!', 'success');
    }
  }, [formData, existingImageUrl, getThumbnailUrl, showToast]);

  // ============================================
  // GENERATE TAG SUGGESTIONS
  // ============================================

  const generateTagSuggestions = () => {
    setSuggestionLoading(true);
    const suggestions = new Set();

    if (formData.name) {
      const titleWords = formData.name.toLowerCase().split(' ');
      titleWords.forEach(word => {
        if (word.length > 3) suggestions.add(word);
      });
    }

    if (formData.description) {
      const descWords = formData.description.toLowerCase().split(' ');
      const commonWords = [
        'the',
        'be',
        'to',
        'of',
        'and',
        'a',
        'in',
        'that',
        'have',
        'i',
        'it',
        'for',
        'not',
        'on',
        'with',
        'he',
        'as',
        'you',
        'do',
        'at',
        'this',
        'but',
        'his',
        'by',
        'from',
        'they',
        'we',
        'say',
        'her',
        'she',
        'or',
        'an',
        'will',
        'my',
        'one',
        'all',
        'would',
        'there',
        'their',
      ];

      descWords.forEach(word => {
        const cleanWord = word.replace(/[^a-zA-Z]/g, '').toLowerCase();
        if (cleanWord.length > 3 && !commonWords.includes(cleanWord)) {
          suggestions.add(cleanWord);
        }
      });
    }

    const categoryMap = {
      branding: [
        'branding',
        'identity',
        'logo',
        'visual-identity',
        'brand-guidelines',
      ],
      product: ['product', 'ui-ux', 'design', 'mockup', 'prototype'],
      others: ['creative', 'art', 'illustration', 'animation', 'experimental'],
    };
    const categoryTags = categoryMap[formData.category] || [
      'design',
      'creative',
    ];
    categoryTags.forEach(tag => suggestions.add(tag));

    const existingTags = new Set(formData.tags);
    const finalSuggestions = Array.from(suggestions)
      .filter(tag => !existingTags.has(tag))
      .slice(0, 8);

    setSuggestedTags(finalSuggestions);
    setSuggestionLoading(false);
    setShowSuggestions(true);

    setTimeout(() => {
      if (!showSuggestions) return;
      setShowSuggestions(false);
    }, 8000);
  };

  // ============================================
  // EFFECTS
  // ============================================

  // Load service data when editing
  useEffect(() => {
    if (service) {
      setFormData({
        name: service.name || '',
        description: service.description || '',
        price: service.price || '',
        duration: service.duration || '',
        category: service.category || 'branding',
        features: service.features || [],
        popular: service.popular || false,
        icon: service.icon || 'FiPackage',
        imageFile: null,
        order: service.order || 0,
        status: service.status || 'active',
        tags: service.tags || [],
        targetAudience: service.targetAudience || [],
        deliverables: service.deliverables || [],
        revisions: service.revisions || '',
        support: service.support || '',
        seoTitle: service.seoTitle || '',
        seoDescription: service.seoDescription || '',
        seoKeywords: service.seoKeywords || [],
        ogTitle: service.ogTitle || '',
        ogDescription: service.ogDescription || '',
        ogImage: service.ogImage || '',
        twitterTitle: service.twitterTitle || '',
        twitterDescription: service.twitterDescription || '',
        twitterImage: service.twitterImage || '',
      });
      setExistingImageUrl(service.imageUrl || null);

      // Set image items from service
      if (service.imageUrl) {
        setImageItems([
          {
            id: 'existing_0',
            url: service.imageUrl,
            status: 'uploaded',
            isExisting: true,
            order: 0,
            publicId: service.imagePublicId || null,
            uploadedUrl: service.imageUrl,
            uploadProgress: 100,
          },
        ]);
        setThumbnailIndex(0);
      }
      setErrors({});
    } else {
      resetForm();
    }
  }, [service, isOpen]);

  // Auto-generate SEO when name or description changes
  useEffect(() => {
    if (formData.name && formData.description) {
      const timer = setTimeout(() => {
        autoGenerateSEOFields();
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [formData.name, formData.description, autoGenerateSEOFields]);

  // Auto-generate tag suggestions
  useEffect(() => {
    if (formData.name && formData.description) {
      const timer = setTimeout(() => {
        generateTagSuggestions();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [formData.name, formData.description, formData.category]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height =
        textareaRef.current.scrollHeight + 'px';
    }
  }, [formData.description]);

  // ESC key handler
  useEffect(() => {
    const handleEsc = e => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  // Body scroll lock
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // ============================================
  // HANDLERS
  // ============================================

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      duration: '',
      category: 'branding',
      features: [],
      popular: false,
      icon: 'FiPackage',
      imageFile: null,
      order: 0,
      status: 'active',
      tags: [],
      targetAudience: [],
      deliverables: [],
      revisions: '',
      support: '',
      seoTitle: '',
      seoDescription: '',
      seoKeywords: [],
      ogTitle: '',
      ogDescription: '',
      ogImage: '',
      twitterTitle: '',
      twitterDescription: '',
      twitterImage: '',
    });
    setImageItems([]);
    setThumbnailIndex(-1);
    setExistingImageUrl(null);
    setErrors({});
    setNewFeature('');
    setNewTag('');
    setNewAudience('');
    setNewDeliverable('');
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Service name is required';
    if (!formData.description.trim())
      newErrors.description = 'Description is required';
    if (!formData.price) newErrors.price = 'Price is required';
    if (!formData.duration) newErrors.duration = 'Duration is required';
    if (!formData.category) newErrors.category = 'Category is required';
    if (formData.features.length === 0)
      newErrors.features = 'At least one feature is required';

    const hasPending = imageItems.some(
      img => img.status === 'pending' || img.status === 'uploading',
    );
    if (hasPending) {
      newErrors.image =
        'Please upload all pending images before saving. Click the upload button on each pending image.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleImageUpload = async (file, tempId) => {
    setIsUploadingImage(true);
    setUploadingImageId(tempId);

    try {
      if (onUploadImage) {
        const result = await onUploadImage(file, tempId);
        return result;
      }
      throw new Error('Upload function not available');
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    } finally {
      setIsUploadingImage(false);
      setUploadingImageId(null);
    }
  };

  const handleImageDelete = async (imageIndexOrPublicId, isTemp = false) => {
    if (isTemp) {
      const publicId = imageIndexOrPublicId;
      try {
        if (onDeleteTempImage) {
          await onDeleteTempImage(publicId);
        }
        const newImages = imageItems.filter(img => img.publicId !== publicId);
        setImageItems(newImages);
        if (thumbnailIndex >= newImages.length) {
          setThumbnailIndex(newImages.length > 0 ? 0 : -1);
        }
        return;
      } catch (error) {
        setErrors(prev => ({
          ...prev,
          image: 'Failed to delete image from Cloudinary',
        }));
        return;
      }
    }

    const imageIndex = imageIndexOrPublicId;
    const itemToDelete = imageItems[imageIndex];
    if (!itemToDelete) return;

    if (itemToDelete?.isExisting && serviceId && onDeleteImage) {
      await onDeleteImage(serviceId, imageIndex);
      const newImages = imageItems.filter((_, i) => i !== imageIndex);
      setImageItems(newImages);
      updateThumbnailAfterDelete(newImages, imageIndex);
      return;
    }

    if (
      itemToDelete?.publicId &&
      itemToDelete?.status === 'uploaded' &&
      !itemToDelete?.isExisting
    ) {
      try {
        if (onDeleteTempImage) {
          await onDeleteTempImage(itemToDelete.publicId);
        } else if (onDeleteImage) {
          await onDeleteImage(itemToDelete.publicId, true);
        }
      } catch (error) {
        setErrors(prev => ({
          ...prev,
          image: 'Failed to delete image from Cloudinary',
        }));
        return;
      }
    }

    const newImages = imageItems.filter((_, i) => i !== imageIndex);
    setImageItems(newImages);
    updateThumbnailAfterDelete(newImages, imageIndex);
  };

  const updateThumbnailAfterDelete = (newImages, deletedIndex) => {
    if (thumbnailIndex >= newImages.length) {
      setThumbnailIndex(newImages.length > 0 ? 0 : -1);
    } else if (thumbnailIndex === deletedIndex) {
      setThumbnailIndex(newImages.length > 0 ? 0 : -1);
    } else if (thumbnailIndex > deletedIndex) {
      setThumbnailIndex(thumbnailIndex - 1);
    }
  };

  const handleImagesChange = newImages => {
    setImageItems(newImages);
    setErrors(prev => ({ ...prev, image: null }));
  };

  const handleThumbnailChange = index => {
    setThumbnailIndex(index);
  };

  const handleTagsChange = newTags => {
    setFormData(prev => ({ ...prev, tags: newTags }));
    setErrors(prev => ({ ...prev, tags: null }));
  };

  const handleAddSuggestion = tag => {
    if (!formData.tags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag],
      }));
    }
  };

  const handleAddAllSuggestions = () => {
    const newTags = [...formData.tags];
    let addedCount = 0;

    suggestedTags.forEach(tag => {
      if (!newTags.includes(tag)) {
        newTags.push(tag);
        addedCount++;
      }
    });

    if (addedCount > 0) {
      setFormData(prev => ({ ...prev, tags: newTags }));
      setShowSuggestions(false);
      showToast(`Added ${addedCount} tag suggestions!`, 'success');
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!validateForm()) return;

    // Auto-generate SEO before submit
    autoGenerateSEOFields();

    const formDataToSend = new FormData();
    const fields = {
      name: formData.name.trim(),
      description: formData.description.trim(),
      category: formData.category,
      price: formData.price,
      duration: formData.duration,
      features: JSON.stringify(formData.features),
      popular: formData.popular,
      icon: formData.icon,
      order: formData.order,
      status: formData.status,
      tags: JSON.stringify(formData.tags),
      targetAudience: JSON.stringify(formData.targetAudience),
      deliverables: JSON.stringify(formData.deliverables),
      revisions: formData.revisions || '',
      support: formData.support || '',
      // SEO fields
      seoTitle: formData.seoTitle || '',
      seoDescription: formData.seoDescription || '',
      seoKeywords: JSON.stringify(formData.seoKeywords || []),
      ogTitle: formData.ogTitle || '',
      ogDescription: formData.ogDescription || '',
      ogImage: formData.ogImage || '',
      twitterTitle: formData.twitterTitle || '',
      twitterDescription: formData.twitterDescription || '',
      twitterImage: formData.twitterImage || '',
    };

    Object.entries(fields).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        formDataToSend.append(key, value);
      }
    });

    // Get image file or URL
    const uploadedImages = imageItems.filter(
      img => img.status === 'uploaded' && img.uploadedUrl,
    );
    const pendingFiles = imageItems.filter(
      img => img.status === 'pending' && img.file,
    );

    if (pendingFiles.length > 0) {
      for (const img of pendingFiles) {
        formDataToSend.append('images', img.file);
      }
    } else if (uploadedImages.length > 0 && !isEditing) {
      // For create with existing URLs (from Cloudinary)
      const urls = uploadedImages.map(img => img.uploadedUrl);
      formDataToSend.append('imageUrls', JSON.stringify(urls));
    }

    // For edit, if there's a new image file
    if (isEditing && pendingFiles.length > 0) {
      for (const img of pendingFiles) {
        formDataToSend.append('image', img.file);
      }
    }

    await onSave(formDataToSend);
  };

  // ============================================
  // BULK INPUT HANDLERS
  // ============================================

  const handleFeatureKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (newFeature.includes(',') || newFeature.includes('#')) {
        const newItems = parseBulkInput(newFeature);
        if (newItems.length > 0) {
          setFormData(prev => ({
            ...prev,
            features: [...new Set([...prev.features, ...newItems])],
          }));
          setNewFeature('');
        }
      } else if (newFeature.trim()) {
        setFormData(prev => ({
          ...prev,
          features: [...prev.features, newFeature.trim()],
        }));
        setNewFeature('');
      }
    }
  };

  const handleAddFeature = e => {
    if (e) e.preventDefault();
    if (newFeature.trim()) {
      if (newFeature.includes(',') || newFeature.includes('#')) {
        const newItems = parseBulkInput(newFeature);
        if (newItems.length > 0) {
          setFormData(prev => ({
            ...prev,
            features: [...new Set([...prev.features, ...newItems])],
          }));
        }
      } else {
        setFormData(prev => ({
          ...prev,
          features: [...prev.features, newFeature.trim()],
        }));
      }
      setNewFeature('');
    }
  };

  const handleRemoveFeature = index => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }));
  };

  const handleTagKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (
        newTag.includes(',') ||
        newTag.includes('#') ||
        newTag.includes(' ')
      ) {
        const newItems = parseBulkInput(newTag);
        if (newItems.length > 0) {
          setFormData(prev => ({
            ...prev,
            tags: [...new Set([...prev.tags, ...newItems])],
          }));
          setNewTag('');
        }
      } else if (newTag.trim()) {
        setFormData(prev => ({
          ...prev,
          tags: [...prev.tags, newTag.trim()],
        }));
        setNewTag('');
      }
    }
  };

  const handleAddTag = e => {
    if (e) e.preventDefault();
    if (newTag.trim()) {
      if (
        newTag.includes(',') ||
        newTag.includes('#') ||
        newTag.includes(' ')
      ) {
        const newItems = parseBulkInput(newTag);
        if (newItems.length > 0) {
          setFormData(prev => ({
            ...prev,
            tags: [...new Set([...prev.tags, ...newItems])],
          }));
        }
      } else {
        setFormData(prev => ({
          ...prev,
          tags: [...prev.tags, newTag.trim()],
        }));
      }
      setNewTag('');
    }
  };

  const handleRemoveTag = index => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter((_, i) => i !== index),
    }));
  };

  const handleAudienceKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (newAudience.includes(',') || newAudience.includes('#')) {
        const newItems = parseBulkInput(newAudience);
        if (newItems.length > 0) {
          setFormData(prev => ({
            ...prev,
            targetAudience: [...new Set([...prev.targetAudience, ...newItems])],
          }));
          setNewAudience('');
        }
      } else if (newAudience.trim()) {
        setFormData(prev => ({
          ...prev,
          targetAudience: [...prev.targetAudience, newAudience.trim()],
        }));
        setNewAudience('');
      }
    }
  };

  const handleAddAudience = e => {
    if (e) e.preventDefault();
    if (newAudience.trim()) {
      if (newAudience.includes(',') || newAudience.includes('#')) {
        const newItems = parseBulkInput(newAudience);
        if (newItems.length > 0) {
          setFormData(prev => ({
            ...prev,
            targetAudience: [...new Set([...prev.targetAudience, ...newItems])],
          }));
        }
      } else {
        setFormData(prev => ({
          ...prev,
          targetAudience: [...prev.targetAudience, newAudience.trim()],
        }));
      }
      setNewAudience('');
    }
  };

  const handleRemoveAudience = index => {
    setFormData(prev => ({
      ...prev,
      targetAudience: prev.targetAudience.filter((_, i) => i !== index),
    }));
  };

  const handleDeliverableKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (newDeliverable.includes(',') || newDeliverable.includes('#')) {
        const newItems = parseBulkInput(newDeliverable);
        if (newItems.length > 0) {
          setFormData(prev => ({
            ...prev,
            deliverables: [...new Set([...prev.deliverables, ...newItems])],
          }));
          setNewDeliverable('');
        }
      } else if (newDeliverable.trim()) {
        setFormData(prev => ({
          ...prev,
          deliverables: [...prev.deliverables, newDeliverable.trim()],
        }));
        setNewDeliverable('');
      }
    }
  };

  const handleAddDeliverable = e => {
    if (e) e.preventDefault();
    if (newDeliverable.trim()) {
      if (newDeliverable.includes(',') || newDeliverable.includes('#')) {
        const newItems = parseBulkInput(newDeliverable);
        if (newItems.length > 0) {
          setFormData(prev => ({
            ...prev,
            deliverables: [...new Set([...prev.deliverables, ...newItems])],
          }));
        }
      } else {
        setFormData(prev => ({
          ...prev,
          deliverables: [...prev.deliverables, newDeliverable.trim()],
        }));
      }
      setNewDeliverable('');
    }
  };

  const handleRemoveDeliverable = index => {
    setFormData(prev => ({
      ...prev,
      deliverables: prev.deliverables.filter((_, i) => i !== index),
    }));
  };

  const handleImageChange = file => {
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setFormData(prev => ({ ...prev, imageFile: file }));
      setExistingImageUrl(previewUrl);
    }
  };

  // ============================================
  // RENDER HELPERS
  // ============================================

  const getStatusBadge = status => {
    if (status === 'active') {
      return (
        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
          ● Active
        </span>
      );
    }
    return (
      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-800/50 dark:text-gray-400">
        ○ Draft
      </span>
    );
  };

  // ============================================
  // RENDER
  // ============================================

  if (!isOpen) return null;

  const isDark = actualTheme === 'dark';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={e => {
        if (e.target === e.currentTarget) return;
      }}
    >
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />

      <div
        ref={modalRef}
        className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl animate-fade-in-up"
        style={{ backgroundColor: 'var(--bg)' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="sticky top-0 z-10 flex justify-between items-center p-5 border-b"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
        >
          <div>
            <h2 className="text-xl font-bold" style={{ color: 'var(--text)' }}>
              {isEditing ? 'Edit Service' : 'Create New Service'}
            </h2>
            <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>
              {isEditing
                ? 'Update your service details and offerings'
                : 'Add a new ready-made service package'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full transition-colors hover:bg-black/5 dark:hover:bg-white/5"
            aria-label="Close modal"
          >
            <FiX className="w-5 h-5" style={{ color: 'var(--text)' }} />
          </button>
        </div>

        {/* Stats Bar - Shows for editing */}
        {isEditing && service && (
          <div
            className="px-5 py-3 border-b flex flex-wrap items-center gap-4 text-sm"
            style={{
              backgroundColor: 'var(--border-light)',
              borderColor: 'var(--border)',
            }}
          >
            <div className="flex items-center gap-2">
              <span className="text-xs" style={{ color: 'var(--muted)' }}>
                Status:
              </span>
              {getStatusBadge(service.status)}
            </div>
            {service.popular && (
              <div className="flex items-center gap-2">
                <FiStar className="w-3 h-3 text-yellow-500" />
                <span className="text-xs font-medium text-yellow-500">
                  Most Popular
                </span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <span className="text-xs" style={{ color: 'var(--muted)' }}>
                Order:
              </span>
              <span
                className="text-xs font-medium"
                style={{ color: 'var(--text)' }}
              >
                {service.order || 0}
              </span>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* ========================================== */}
          {/* BASIC INFORMATION */}
          {/* ========================================== */}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-4">
              {/* Service Name */}
              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: 'var(--text)' }}
                >
                  Service Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => {
                    setFormData(prev => ({ ...prev, name: e.target.value }));
                    setErrors(prev => ({ ...prev, name: null }));
                  }}
                  required
                  className={`w-full px-4 py-2 rounded-xl outline-none transition-all ${
                    errors.name ? 'border-red-500' : ''
                  }`}
                  style={{
                    backgroundColor: 'transparent',
                    border: `1px solid ${errors.name ? '#ef4444' : 'var(--border)'}`,
                    color: 'var(--text)',
                  }}
                  placeholder="e.g., Brand Identity Package"
                  maxLength="100"
                />
                {errors.name && (
                  <p className="text-xs text-red-500 mt-1">{errors.name}</p>
                )}
              </div>

              {/* Description */}
              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: 'var(--text)' }}
                >
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  ref={textareaRef}
                  rows="3"
                  value={formData.description}
                  onChange={e => {
                    setFormData(prev => ({
                      ...prev,
                      description: e.target.value,
                    }));
                    setErrors(prev => ({ ...prev, description: null }));
                  }}
                  required
                  className={`w-full px-4 py-2 rounded-xl outline-none transition-all resize-none ${
                    errors.description ? 'border-red-500' : ''
                  }`}
                  style={{
                    backgroundColor: 'transparent',
                    border: `1px solid ${errors.description ? '#ef4444' : 'var(--border)'}`,
                    color: 'var(--text)',
                  }}
                  placeholder="Describe what this service includes..."
                />
                {errors.description && (
                  <p className="text-xs text-red-500 mt-1">
                    {errors.description}
                  </p>
                )}
              </div>

              {/* Price & Duration */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    className="block text-sm font-medium mb-1"
                    style={{ color: 'var(--text)' }}
                  >
                    Price <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.price}
                    onChange={e => {
                      setFormData(prev => ({ ...prev, price: e.target.value }));
                      setErrors(prev => ({ ...prev, price: null }));
                    }}
                    required
                    className={`w-full px-4 py-2 rounded-xl outline-none transition-all ${
                      errors.price ? 'border-red-500' : ''
                    }`}
                    style={{
                      backgroundColor: 'transparent',
                      border: `1px solid ${errors.price ? '#ef4444' : 'var(--border)'}`,
                      color: 'var(--text)',
                    }}
                    placeholder="e.g., 2,500"
                  />
                  {errors.price && (
                    <p className="text-xs text-red-500 mt-1">{errors.price}</p>
                  )}
                </div>
                <div>
                  <label
                    className="block text-sm font-medium mb-1"
                    style={{ color: 'var(--text)' }}
                  >
                    Duration <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.duration}
                    onChange={e => {
                      setFormData(prev => ({
                        ...prev,
                        duration: e.target.value,
                      }));
                      setErrors(prev => ({ ...prev, duration: null }));
                    }}
                    required
                    className={`w-full px-4 py-2 rounded-xl outline-none transition-all ${
                      errors.duration ? 'border-red-500' : ''
                    }`}
                    style={{
                      backgroundColor: 'transparent',
                      border: `1px solid ${errors.duration ? '#ef4444' : 'var(--border)'}`,
                      color: 'var(--text)',
                    }}
                    placeholder="e.g., 2-3 weeks"
                  />
                  {errors.duration && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.duration}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-4">
              {/* Category & Icon */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    className="block text-sm font-medium mb-1"
                    style={{ color: 'var(--text)' }}
                  >
                    Category <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.category}
                    onChange={e => {
                      setFormData(prev => ({
                        ...prev,
                        category: e.target.value,
                      }));
                      setErrors(prev => ({ ...prev, category: null }));
                    }}
                    className={`w-full px-4 py-2 rounded-xl outline-none transition-all ${
                      errors.category ? 'border-red-500' : ''
                    }`}
                    style={{
                      backgroundColor: 'transparent',
                      border: `1px solid ${errors.category ? '#ef4444' : 'var(--border)'}`,
                      color: 'var(--text)',
                    }}
                  >
                    {CATEGORY_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.icon} {opt.label}
                      </option>
                    ))}
                  </select>
                  {errors.category && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.category}
                    </p>
                  )}
                </div>
                <div>
                  <label
                    className="block text-sm font-medium mb-1"
                    style={{ color: 'var(--text)' }}
                  >
                    Icon
                  </label>
                  <select
                    value={formData.icon}
                    onChange={e =>
                      setFormData(prev => ({ ...prev, icon: e.target.value }))
                    }
                    className="w-full px-4 py-2 rounded-xl outline-none transition-all"
                    style={{
                      backgroundColor: 'transparent',
                      border: '1px solid var(--border)',
                      color: 'var(--text)',
                    }}
                  >
                    {ICON_OPTIONS.map(opt => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Order & Status */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    className="block text-sm font-medium mb-1"
                    style={{ color: 'var(--text)' }}
                  >
                    Display Order
                  </label>
                  <input
                    type="number"
                    value={formData.order}
                    onChange={e =>
                      setFormData(prev => ({
                        ...prev,
                        order: parseInt(e.target.value) || 0,
                      }))
                    }
                    className="w-full px-4 py-2 rounded-xl outline-none transition-all"
                    style={{
                      backgroundColor: 'transparent',
                      border: '1px solid var(--border)',
                      color: 'var(--text)',
                    }}
                  />
                </div>
                <div>
                  <label
                    className="block text-sm font-medium mb-1"
                    style={{ color: 'var(--text)' }}
                  >
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={e =>
                      setFormData(prev => ({ ...prev, status: e.target.value }))
                    }
                    className="w-full px-4 py-2 rounded-xl outline-none transition-all"
                    style={{
                      backgroundColor: 'transparent',
                      border: '1px solid var(--border)',
                      color: 'var(--text)',
                    }}
                  >
                    {STATUS_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Popular & Image Upload */}
              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.popular}
                    onChange={e =>
                      setFormData(prev => ({
                        ...prev,
                        popular: e.target.checked,
                      }))
                    }
                    className="w-4 h-4 rounded"
                  />
                  <span
                    className="text-sm font-medium"
                    style={{ color: 'var(--text)' }}
                  >
                    <FiStar className="inline w-3.5 h-3.5 text-yellow-500 mr-1" />
                    Mark as Popular
                  </span>
                </label>
              </div>

              {/* Revisions & Support */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    className="block text-sm font-medium mb-1"
                    style={{ color: 'var(--text)' }}
                  >
                    Revisions
                  </label>
                  <input
                    type="text"
                    value={formData.revisions}
                    onChange={e =>
                      setFormData(prev => ({
                        ...prev,
                        revisions: e.target.value,
                      }))
                    }
                    className="w-full px-4 py-2 rounded-xl outline-none transition-all"
                    style={{
                      backgroundColor: 'transparent',
                      border: '1px solid var(--border)',
                      color: 'var(--text)',
                    }}
                    placeholder="e.g., 3 rounds"
                  />
                </div>
                <div>
                  <label
                    className="block text-sm font-medium mb-1"
                    style={{ color: 'var(--text)' }}
                  >
                    Support
                  </label>
                  <input
                    type="text"
                    value={formData.support}
                    onChange={e =>
                      setFormData(prev => ({
                        ...prev,
                        support: e.target.value,
                      }))
                    }
                    className="w-full px-4 py-2 rounded-xl outline-none transition-all"
                    style={{
                      backgroundColor: 'transparent',
                      border: '1px solid var(--border)',
                      color: 'var(--text)',
                    }}
                    placeholder="e.g., 30 days support"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* ========================================== */}
          {/* IMAGE UPLOAD */}
          {/* ========================================== */}

          <div>
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: 'var(--text)' }}
            >
              Service Image
            </label>
            <ImageUploader
              images={imageItems}
              onImagesChange={handleImagesChange}
              thumbnailIndex={thumbnailIndex}
              onThumbnailChange={handleThumbnailChange}
              onUploadImage={handleImageUpload}
              onDeleteImage={handleImageDelete}
              onDeleteTempImage={onDeleteTempImage}
              isUploading={saving}
              disabled={saving}
              isEditing={isEditing}
              uploadingImageId={uploadingImageId}
            />
            {errors.image && (
              <p className="text-xs text-red-500 mt-2">{errors.image}</p>
            )}
            <div className="text-xs mt-2" style={{ color: 'var(--muted)' }}>
              🎬 Recommended: 16:9 (1920x1080px or 1280x720px)
            </div>
          </div>

          {/* ========================================== */}
          {/* FEATURES */}
          {/* ========================================== */}

          <div
            className="border-t pt-4"
            style={{ borderColor: 'var(--border)' }}
          >
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: 'var(--text)' }}
            >
              Features / What's Included <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={newFeature}
                onChange={e => setNewFeature(e.target.value)}
                onKeyDown={handleFeatureKeyDown}
                placeholder="Add a feature (or paste comma/ hashtag separated list)..."
                className={`flex-1 px-4 py-2 rounded-xl outline-none transition-all ${
                  errors.features ? 'border-red-500' : ''
                }`}
                style={{
                  backgroundColor: 'transparent',
                  border: `1px solid ${errors.features ? '#ef4444' : 'var(--border)'}`,
                  color: 'var(--text)',
                }}
              />
              <button
                type="button"
                onClick={handleAddFeature}
                className="px-4 py-2 rounded-xl transition-all hover:opacity-80"
                style={{ backgroundColor: 'var(--text)', color: 'var(--bg)' }}
              >
                <FiPlus className="w-4 h-4" />
              </button>
            </div>
            {errors.features && (
              <p className="text-xs text-red-500 mt-1">{errors.features}</p>
            )}
            <div className="flex flex-wrap gap-2">
              {formData.features.map((feature, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm"
                  style={{
                    backgroundColor: 'var(--border)',
                    color: 'var(--text)',
                  }}
                >
                  <FiCheckCircle className="w-3 h-3 text-green-500" />
                  {feature}
                  <button
                    type="button"
                    onClick={() => handleRemoveFeature(idx)}
                    className="ml-1 hover:text-red-500"
                  >
                    <FiX className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <p className="text-xs mt-2" style={{ color: 'var(--muted)' }}>
              💡 Tip: Paste multiple items separated by commas or hashtags.
              Press Enter to add.
            </p>
          </div>

          {/* ========================================== */}
          {/* DELIVERABLES */}
          {/* ========================================== */}

          <div
            className="border-t pt-4"
            style={{ borderColor: 'var(--border)' }}
          >
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: 'var(--text)' }}
            >
              Deliverables
            </label>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={newDeliverable}
                onChange={e => setNewDeliverable(e.target.value)}
                onKeyDown={handleDeliverableKeyDown}
                placeholder="Add a deliverable..."
                className="flex-1 px-4 py-2 rounded-xl outline-none transition-all"
                style={{
                  backgroundColor: 'transparent',
                  border: '1px solid var(--border)',
                  color: 'var(--text)',
                }}
              />
              <button
                type="button"
                onClick={handleAddDeliverable}
                className="px-4 py-2 rounded-xl transition-all hover:opacity-80"
                style={{ backgroundColor: 'var(--text)', color: 'var(--bg)' }}
              >
                <FiPlus className="w-4 h-4" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.deliverables.map((item, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm"
                  style={{
                    backgroundColor: 'var(--border)',
                    color: 'var(--text)',
                  }}
                >
                  <FiPackage className="w-3 h-3" />
                  {item}
                  <button
                    type="button"
                    onClick={() => handleRemoveDeliverable(idx)}
                    className="ml-1 hover:text-red-500"
                  >
                    <FiX className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* ========================================== */}
          {/* TAGS - WITH SUGGESTIONS */}
          {/* ========================================== */}

          <div
            className="border-t pt-4"
            style={{ borderColor: 'var(--border)' }}
          >
            <div className="flex items-center justify-between mb-2">
              <label
                className="text-sm font-medium"
                style={{ color: 'var(--text)' }}
              >
                Tags
              </label>
              <button
                type="button"
                onClick={generateTagSuggestions}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg transition-all hover:opacity-80"
                style={{
                  backgroundColor: 'var(--border-light)',
                  color: 'var(--text)',
                }}
              >
                <TfiLightBulb className="w-3.5 h-3.5" />
                Generate Suggestions
              </button>
            </div>

            {showSuggestions && suggestedTags.length > 0 && (
              <div
                className="mb-3 p-3 rounded-xl border animate-fade-in-up"
                style={{
                  backgroundColor: 'var(--border-light)',
                  borderColor: 'var(--border)',
                }}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <TfiLightBulb className="w-4 h-4 text-yellow-500" />
                    <span
                      className="text-sm font-medium"
                      style={{ color: 'var(--text)' }}
                    >
                      Creative Suggestions
                    </span>
                    <span className="text-xs" style={{ color: 'var(--muted)' }}>
                      Based on your service
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleAddAllSuggestions}
                      className="text-xs px-2 py-1 rounded hover:opacity-80"
                      style={{
                        backgroundColor: 'var(--bg)',
                        color: 'var(--text)',
                      }}
                    >
                      Add All
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowSuggestions(false)}
                      className="text-xs hover:opacity-80"
                      style={{ color: 'var(--muted)' }}
                    >
                      <FiX className="w-3 h-3" />
                    </button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {suggestionLoading ? (
                    <div className="flex items-center gap-2 py-1">
                      <div className="w-4 h-4 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
                      <span
                        className="text-xs"
                        style={{ color: 'var(--muted)' }}
                      >
                        Generating suggestions...
                      </span>
                    </div>
                  ) : (
                    suggestedTags.map((tag, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleAddSuggestion(tag)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition-all hover:opacity-80 group"
                        style={{
                          backgroundColor: 'var(--bg)',
                          border: '1px solid var(--border)',
                          color: 'var(--text)',
                        }}
                      >
                        <FiTag className="w-3 h-3" />
                        {tag}
                        <FiPlus className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}

            <TagManager
              selectedTags={formData.tags}
              onTagsChange={handleTagsChange}
              existingTags={[]}
              maxTags={20}
              variant="compact"
              placeholder="Add service tags..."
            />
          </div>

          {/* ========================================== */}
          {/* TARGET AUDIENCE */}
          {/* ========================================== */}

          <div
            className="border-t pt-4"
            style={{ borderColor: 'var(--border)' }}
          >
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: 'var(--text)' }}
            >
              Target Audience
            </label>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={newAudience}
                onChange={e => setNewAudience(e.target.value)}
                onKeyDown={handleAudienceKeyDown}
                placeholder="Add target audience..."
                className="flex-1 px-4 py-2 rounded-xl outline-none transition-all"
                style={{
                  backgroundColor: 'transparent',
                  border: '1px solid var(--border)',
                  color: 'var(--text)',
                }}
              />
              <button
                type="button"
                onClick={handleAddAudience}
                className="px-4 py-2 rounded-xl transition-all hover:opacity-80"
                style={{ backgroundColor: 'var(--text)', color: 'var(--bg)' }}
              >
                <FiPlus className="w-4 h-4" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.targetAudience.map((audience, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm"
                  style={{
                    backgroundColor: 'var(--border)',
                    color: 'var(--text)',
                  }}
                >
                  <FiUsers className="w-3 h-3" />
                  {audience}
                  <button
                    type="button"
                    onClick={() => handleRemoveAudience(idx)}
                    className="ml-1 hover:text-red-500"
                  >
                    <FiX className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* ========================================== */}
          {/* SEO & OG SECTION */}
          {/* ========================================== */}

          <div
            className="border-t pt-4 space-y-4"
            style={{ borderColor: 'var(--border)' }}
          >
            <div className="flex items-center gap-2">
              <FiTrendingUp className="w-4 h-4 text-blue-500" />
              <h3
                className="text-sm font-semibold"
                style={{ color: 'var(--text)' }}
              >
                SEO & Social Sharing
              </h3>
              <button
                type="button"
                onClick={autoGenerateSEOFields}
                className="px-2 py-0.5 text-[10px] rounded transition-all hover:opacity-80"
                style={{
                  backgroundColor: 'var(--border)',
                  color: 'var(--text)',
                }}
              >
                <FiCopy className="inline w-3 h-3 mr-1" />
                Auto-generate
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* SEO Title */}
              <div>
                <label
                  className="block text-[10px] font-medium mb-1"
                  style={{ color: 'var(--text)' }}
                >
                  SEO Title
                  <span
                    className="ml-1 text-[9px]"
                    style={{ color: 'var(--muted)' }}
                  >
                    (50-60 chars)
                  </span>
                </label>
                <input
                  type="text"
                  value={formData.seoTitle}
                  onChange={e =>
                    setFormData(prev => ({ ...prev, seoTitle: e.target.value }))
                  }
                  className="w-full px-3 py-1.5 rounded-lg text-sm outline-none transition-all"
                  style={{
                    backgroundColor: 'transparent',
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                  }}
                  placeholder="SEO Title"
                  maxLength="60"
                />
              </div>

              {/* OG Title */}
              <div>
                <label
                  className="block text-[10px] font-medium mb-1"
                  style={{ color: 'var(--text)' }}
                >
                  OG Title (Facebook/LinkedIn)
                  <span
                    className="ml-1 text-[9px]"
                    style={{ color: 'var(--muted)' }}
                  >
                    (60-90 chars)
                  </span>
                </label>
                <input
                  type="text"
                  value={formData.ogTitle}
                  onChange={e =>
                    setFormData(prev => ({ ...prev, ogTitle: e.target.value }))
                  }
                  className="w-full px-3 py-1.5 rounded-lg text-sm outline-none transition-all"
                  style={{
                    backgroundColor: 'transparent',
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                  }}
                  placeholder="OG Title"
                  maxLength="95"
                />
              </div>

              {/* SEO Description */}
              <div>
                <label
                  className="block text-[10px] font-medium mb-1"
                  style={{ color: 'var(--text)' }}
                >
                  SEO Description
                  <span
                    className="ml-1 text-[9px]"
                    style={{ color: 'var(--muted)' }}
                  >
                    (150-160 chars)
                  </span>
                </label>
                <textarea
                  value={formData.seoDescription}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      seoDescription: e.target.value,
                    }))
                  }
                  rows="2"
                  className="w-full px-3 py-1.5 rounded-lg text-sm outline-none transition-all resize-none"
                  style={{
                    backgroundColor: 'transparent',
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                  }}
                  placeholder="SEO Description"
                  maxLength="160"
                />
              </div>

              {/* OG Description */}
              <div>
                <label
                  className="block text-[10px] font-medium mb-1"
                  style={{ color: 'var(--text)' }}
                >
                  OG Description
                  <span
                    className="ml-1 text-[9px]"
                    style={{ color: 'var(--muted)' }}
                  >
                    (150-200 chars)
                  </span>
                </label>
                <textarea
                  value={formData.ogDescription}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      ogDescription: e.target.value,
                    }))
                  }
                  rows="2"
                  className="w-full px-3 py-1.5 rounded-lg text-sm outline-none transition-all resize-none"
                  style={{
                    backgroundColor: 'transparent',
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                  }}
                  placeholder="OG Description"
                  maxLength="200"
                />
              </div>

              {/* OG Image */}
              <div>
                <label
                  className="block text-[10px] font-medium mb-1"
                  style={{ color: 'var(--text)' }}
                >
                  OG Image URL
                  <span
                    className="ml-1 text-[9px]"
                    style={{ color: 'var(--muted)' }}
                  >
                    (Auto-filled from image)
                  </span>
                </label>
                <input
                  type="text"
                  value={formData.ogImage}
                  onChange={e =>
                    setFormData(prev => ({ ...prev, ogImage: e.target.value }))
                  }
                  className="w-full px-3 py-1.5 rounded-lg text-sm outline-none transition-all"
                  style={{
                    backgroundColor: 'transparent',
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                  }}
                  placeholder="https://res.cloudinary.com/..."
                />
              </div>

              {/* Twitter Image */}
              <div>
                <label
                  className="block text-[10px] font-medium mb-1"
                  style={{ color: 'var(--text)' }}
                >
                  Twitter Image URL
                  <span
                    className="ml-1 text-[9px]"
                    style={{ color: 'var(--muted)' }}
                  >
                    (Auto-filled from OG)
                  </span>
                </label>
                <input
                  type="text"
                  value={formData.twitterImage}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      twitterImage: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-1.5 rounded-lg text-sm outline-none transition-all"
                  style={{
                    backgroundColor: 'transparent',
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                  }}
                  placeholder="https://res.cloudinary.com/..."
                />
              </div>

              {/* SEO Keywords */}
              <div>
                <label
                  className="block text-[10px] font-medium mb-1"
                  style={{ color: 'var(--text)' }}
                >
                  SEO Keywords
                  <span
                    className="ml-1 text-[9px]"
                    style={{ color: 'var(--muted)' }}
                  >
                    (5-10 keywords)
                  </span>
                </label>
                <input
                  type="text"
                  value={formData.seoKeywords.join(', ')}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      seoKeywords: e.target.value
                        .split(',')
                        .map(k => k.trim())
                        .filter(k => k),
                    }))
                  }
                  className="w-full px-3 py-1.5 rounded-lg text-sm outline-none transition-all"
                  style={{
                    backgroundColor: 'transparent',
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                  }}
                  placeholder="keyword1, keyword2, keyword3"
                />
              </div>

              {/* Twitter Title */}
              <div>
                <label
                  className="block text-[10px] font-medium mb-1"
                  style={{ color: 'var(--text)' }}
                >
                  Twitter Title
                  <span
                    className="ml-1 text-[9px]"
                    style={{ color: 'var(--muted)' }}
                  >
                    (Auto-filled from OG)
                  </span>
                </label>
                <input
                  type="text"
                  value={formData.twitterTitle}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      twitterTitle: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-1.5 rounded-lg text-sm outline-none transition-all"
                  style={{
                    backgroundColor: 'transparent',
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                  }}
                  placeholder="Twitter Title"
                />
              </div>
            </div>

            {/* Social Preview Indicator */}
            <SocialPreviewIndicator
              ogImage={formData.ogImage}
              ogTitle={formData.ogTitle}
              ogDescription={formData.ogDescription}
            />

            <div
              className="p-2 rounded-lg text-[10px]"
              style={{
                backgroundColor: isDark
                  ? 'rgba(59, 130, 246, 0.08)'
                  : 'rgba(59, 130, 246, 0.04)',
                border: '1px solid rgba(59, 130, 246, 0.1)',
              }}
            >
              <div className="flex items-center gap-2">
                <FiInfo className="w-3 h-3 text-blue-500 flex-shrink-0" />
                <span
                  style={{
                    color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.5)',
                  }}
                >
                  These fields are stored in the database and used by the server
                  to generate OG tags when your service is shared on social
                  media.
                </span>
              </div>
            </div>
          </div>

          {/* ========================================== */}
          {/* ACTION BUTTONS */}
          {/* ========================================== */}

          <div
            className="flex gap-3 pt-4 border-t"
            style={{ borderColor: 'var(--border)' }}
          >
            <button
              type="submit"
              disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-medium transition-all hover:opacity-80 disabled:opacity-50"
              style={{ backgroundColor: 'var(--text)', color: 'var(--bg)' }}
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <FiSave className="w-4 h-4" />
                  {isEditing ? 'Update Service' : 'Create Service'}
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-5 py-2.5 rounded-xl font-medium transition-all hover:opacity-80"
              style={{
                border: '1px solid var(--border)',
                color: 'var(--text)',
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>

      {/* CSS Animation */}
      <style jsx>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.98);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.25s ease-out forwards;
        }
      `}</style>
    </div>
  );
}

export default ServiceModal;
